/**
 * Local storage implementation for salary slip uploads
 * This is a fallback mechanism when the server API fails
 */

// Store for keeping track of uploaded files
const STORAGE_KEY = 'payns-local-salary-slips';

/**
 * Initialize local storage if needed
 */
const initLocalStorage = () => {
  if (!localStorage.getItem(STORAGE_KEY)) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
  }
};

/**
 * Upload a salary slip to browser local storage
 * @param {Object} file - File object
 * @param {String} nik - Employee NIK
 * @param {String} month - Month (1-12)
 * @param {String} year - Year (4 digits)
 * @returns {Promise<Object>} - Upload result
 */
const uploadSalarySlip = async (file, nik, month, year, additionalData = {}) => {
  return new Promise((resolve, reject) => {
    try {
      // Read the file as data URL
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const dataUrl = e.target.result;
          
          // Get existing data
          initLocalStorage();
          const slips = JSON.parse(localStorage.getItem(STORAGE_KEY));
          
          // Create a unique ID
          const id = `${nik}_${month}_${year}_${new Date().getTime()}`;
          
          // Create file path like the server would
          const monthPadded = month.toString().padStart(2, '0');
          const fileName = `ht/slipgaji/${year.substring(2)}${monthPadded}/${nik}_${monthPadded}_${year}.pdf`;
          
          // Create the entry
          const newSlip = {
            id,
            nik,
            month: monthPadded, 
            year,
            fileName,
            dataUrl,
            originalName: file.name,
            size: file.size,
            type: file.type,
            uploadDate: new Date().toISOString(),
            local: true,
            ...additionalData
          };
          
          // Check for duplicates
          const existingIndex = slips.findIndex(
            (slip) => slip.nik === nik && slip.month === monthPadded && slip.year === year
          );
          
          if (existingIndex !== -1) {
            // Replace existing entry
            slips[existingIndex] = newSlip;
            console.log(`Replaced existing local salary slip for ${nik} - ${monthPadded}/${year}`);
          } else {
            // Add new entry
            slips.push(newSlip);
            console.log(`Added new local salary slip for ${nik} - ${monthPadded}/${year}`);
          }
          
          // Save back to local storage
          localStorage.setItem(STORAGE_KEY, JSON.stringify(slips));
          
          // Return result similar to API
          resolve({
            success: true, 
            message: 'Salary slip saved locally',
            data: {
              id,
              nik,
              month: monthPadded,
              year,
              fileName,
              local: true
            }
          });
        } catch (error) {
          console.error('Error saving to local storage:', error);
          reject(error);
        }
      };
      
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        reject(new Error('Failed to read file'));
      };
      
      // Start reading the file
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Local storage upload error:', error);
      reject(error);
    }
  });
};

/**
 * Get all locally stored salary slips
 * @param {String} nik - Employee NIK (optional, filters by NIK if provided)
 * @returns {Array} - Array of salary slip objects
 */
const getSalarySlips = (nik = null) => {
  initLocalStorage();
  let slips = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  
  // Filter by NIK if provided
  if (nik) {
    slips = slips.filter(slip => slip.nik === nik);
  }
  
  return slips;
};

/**
 * Get a specific salary slip
 * @param {String} id - Slip ID
 * @returns {Object|null} - Salary slip object or null if not found
 */
const getSalarySlip = (id) => {
  const slips = getSalarySlips();
  return slips.find(slip => slip.id === id) || null;
};

/**
 * Get salary slip by NIK, month and year
 * @param {String} nik - Employee NIK
 * @param {String} month - Month
 * @param {String} year - Year
 * @returns {Object|null} - Salary slip object or null if not found
 */
const getSalarySlipByPeriod = (nik, month, year) => {
  const monthPadded = month.toString().padStart(2, '0');
  const slips = getSalarySlips();
  return slips.find(
    slip => slip.nik === nik && slip.month === monthPadded && slip.year === year
  ) || null;
};

/**
 * Delete a salary slip
 * @param {String} id - Slip ID
 * @returns {Boolean} - True if deleted, false if not found
 */
const deleteSalarySlip = (id) => {
  initLocalStorage();
  const slips = JSON.parse(localStorage.getItem(STORAGE_KEY));
  const initialLength = slips.length;
  
  const filtered = slips.filter(slip => slip.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  
  return filtered.length < initialLength;
};

export default {
  uploadSalarySlip,
  getSalarySlips,
  getSalarySlip,
  getSalarySlipByPeriod,
  deleteSalarySlip
}; 